import RiskMedicationQuestionnaireBuilder from "./RiskMedicationQuestionnaireBuilder"
import PatientTakesMedicine from "../questions/PatientTakesMedicine"
import PatientHasPreCondition, {
    PatientPreConditions
} from "../questions/PatientHasPreCondition"
import {
    MedicationIndicators,
    medSearch,
    QuestionnaireTimestamp,
    SingleMedicationEntries
} from "pokal_api"
import QuestionCreator from "../abstract/QuestionCreator"
import MedicationQuestion from "../abstract/MedicationQuestion"
import MedicationQuestionnaireInput from "../abstract/MedicationQuestionnaireInput"
import MedicationAnswer from "../abstract/MedicationAnswer"
import BoolQuestionAnswerStrategy from "../abstract/BoolQuestionAnswerStrategy"
import QuestionnaireCreator from "../abstract/QuestionnaireCreator"
import PatientSymptomsImproved from "../questions/PatientSymptomsImproved"
import PatientTakesAtLeastNumberOfAntidepressants from "../questions/PatientTakesAtLeastNumberOfAntidepressants"

const ANTI_DEPRESSANT_SUBSCRIBED =
    (weeks: number, mono: boolean): QuestionCreator =>
    (info: MedicationQuestionnaireInput) =>
        new MedicationQuestion(
            `Wird ein Antidepressivum${mono ? " in Monotherapie" : ""} ≥${String(weeks)} Wochen verschrieben?`,
            info
        )

const IS_AT_LEAST_ONE_ANTI_DEPRESSANT_TAKEN = (
    info: MedicationQuestionnaireInput
) =>
    new MedicationQuestion(
        "Wird im Moment mindestens ein Antidepressivum eingenommen?",
        info
    ).answerAutomatically((info) => {
        if (info.medication.length === 0) {
            return new MedicationAnswer("Nein")
                .triggersIndicatorWithArgs(
                    MedicationIndicators.THERAPY_CONTROL_2,
                    [getPhqScoreOrGetUnknown(info.phqScore)]
                )
                .terminates()
        } else return new MedicationAnswer("Ja")
    })
const getPhqScoreText = (phqScore: number): string => {
    if (phqScore >= 0 && phqScore <= 9) {
        return "unauffälligen"
    } else if (phqScore >= 10 && phqScore <= 14) {
        return "leichten"
    } else if (phqScore >= 15 && phqScore <= 19) {
        return "moderaten"
    } else if (phqScore >= 20 && phqScore <= 27) {
        return "schweren"
    } else {
        return "Fehler (PHQ-Score: " + phqScore + " out of bounds)"
    }
}
const getPhqScoreOrGetUnknown = (phqScore: number | null): string => {
    if (phqScore === null) {
        return "null oder ist nicht erfasst worden"
    } else {
        return phqScore.toString()
    }
}
const IS_PHQ_SCORE_HIGHER_THAN =
    (
        value: number,
        answers?: {
            yes?: MedicationAnswer
            no?: MedicationAnswer
        }
    ): QuestionCreator =>
    (info) =>
        new MedicationQuestion(
            (info) =>
                `Ist der PHQ-${info.timestamp === QuestionnaireTimestamp.T0 ? "9" : "8"} Score höher als ${value}`,
            info
        ).answerAutomatically((info) => {
            if (info.phqScore === null) {
                return new MedicationAnswer("No answer required.")
            }
            if (info.phqScore > value) {
                return answers && answers.yes
                    ? answers.yes
                    : new MedicationAnswer("Ja")
            } else {
                return answers && answers.no
                    ? answers.no
                    : new MedicationAnswer("Nein")
            }
        })

export const IS_PATIENT_YOUNGER_THAN =
    (
        years: number,
        answers?: {
            yes?: MedicationAnswer
            no?: MedicationAnswer
        }
    ): QuestionCreator =>
    (info) =>
        new MedicationQuestion(
            `Ist die Patient:in jünger als ${years} Jahre alt?`,
            info
        ).answerAutomatically((info) => {
            const today = new Date();
            const birthDate = new Date(info.patient.birthdate);
            
            let age = today.getFullYear() - birthDate.getFullYear();
            
            // Adjust age if birthday hasn't occurred yet this year
            const hasBirthdayOccurredThisYear = 
                today.getMonth() > birthDate.getMonth() || 
                (today.getMonth() === birthDate.getMonth() && 
                today.getDate() >= birthDate.getDate());
                
            if (!hasBirthdayOccurredThisYear) {
                age--;
            }
            
            if (age < years) {
                return answers && answers.yes
                    ? answers.yes
                    : new MedicationAnswer("Ja")
            } else {
                return answers && answers.no
                    ? answers.no
                    : new MedicationAnswer("Nein")
            }
        })

const FIRST_EPISODE_OF_LIGHT_DEPRESSION: QuestionCreator = (info) =>
    //Handelt es sich um eine erste Episode einer leichten Depression?
    PatientHasPreCondition(
        PatientPreConditions.v12, //kan nicht mehr getriggert werden weil v12 entfernt wurde
        new MedicationAnswer("Ja").triggers([
            MedicationIndicators.POTENTIAL_OVER_MEDICATION_3
        ])
    )(info)

const FIRST_EPISODE_OF_SEVERE_DEPRESSION: QuestionCreator = (info) =>
    //Handelt es sich um eine erste Episode einer leichten Depression?
    PatientHasPreCondition(
        PatientPreConditions.v12, //kan nicht mehr getriggert werden weil v12 entfernt wurde
        new MedicationAnswer("Ja").followedBy(
            //Leidet der Patient an Demenz?
            PatientHasPreCondition(
                PatientPreConditions.v8,
                new MedicationAnswer("Ja").triggers([
                    MedicationIndicators.POTENTIAL_OVER_MEDICATION_FOR_PRE_SICKNESS_2
                ])
            )(info).followedBy(
                //Leidet der Patient an Herzinsuffizienz?
                PatientHasPreCondition(
                    PatientPreConditions.v4,
                    new MedicationAnswer("Ja").triggers([
                        MedicationIndicators.POTENTIAL_OVER_MEDICATION_FOR_PRE_SICKNESS_1
                    ])
                )(info)
            )
        ),
        {
            strategy: BoolQuestionAnswerStrategy.ON_YES,
            alternativeAnswer: new MedicationAnswer("Nein").followedBy(
                //Handelt es sich um eine erste Episode einer mittelgradigen Depression?
                PatientHasPreCondition(
                    PatientPreConditions.v13, //kan nicht mehr getriggert werden weil v13 entfernt wurde
                    new MedicationAnswer("Ja").followedBy(
                        //Leidet der Patient an Demenz?
                        PatientHasPreCondition(
                            PatientPreConditions.v8,
                            new MedicationAnswer("Ja").triggers([
                                MedicationIndicators.POTENTIAL_OVER_MEDICATION_FOR_PRE_SICKNESS_2
                            ])
                        )(info).followedBy(
                            //Leidet der Patient an Herzinsuffizienz?
                            PatientHasPreCondition(
                                PatientPreConditions.v4,
                                new MedicationAnswer("Ja").triggers([
                                    MedicationIndicators.POTENTIAL_OVER_MEDICATION_FOR_PRE_SICKNESS_1
                                ])
                            )(info)
                        )
                    )
                )(info)
            )
        }
    )(info)

const TWO_OR_MORE_ANTI_DEPRESSANTS: QuestionCreator = (info) =>
    new MedicationQuestion(
        "Werden zwei oder mehr Antidepressiva verschrieben?",
        info
    ).answerAutomatically((info) => {
        if (info.medication.length >= 2)
            return new MedicationAnswer("Ja").followedBy(
                PatientTakesMedicine(
                    medSearch()
                        .and(SingleMedicationEntries.MIRTAZAPIN)
                        .and(SingleMedicationEntries.MIANSERIN)
                        .and(SingleMedicationEntries.TRAZODON),
                    new MedicationAnswer("Nein").triggers([
                        MedicationIndicators.POTENTIAL_OVER_MEDICATION_5
                    ]),
                    { strategy: BoolQuestionAnswerStrategy.ON_NO }
                )(info)
            )
        else return new MedicationAnswer("Nein")
    })

const TWO_OR_MORE_ANTI_DEPRESSANTS_2: QuestionCreator = (info) =>
    new MedicationQuestion(
        "Werden zwei oder mehr Antidepressiva verschrieben?",
        info
    ).answerAutomatically((info) => {
        if (info.medication.length >= 2)
            return new MedicationAnswer("Ja").followedBy(
                //Handelt es sich um eine erste Episode einer mittelgradigen Depression?
                PatientHasPreCondition(
                    PatientPreConditions.v13, //kan nicht mehr getriggert werden weil v13 entfernt wurde
                    new MedicationAnswer("Ja").triggers([
                        MedicationIndicators.POTENTIAL_OVER_MEDICATION_4
                    ])
                )(info)
            )
        else return new MedicationAnswer("Nein")
    })

const PATIENT_HAS_ANXIETY: QuestionCreator = (info) =>
    new MedicationQuestion("Liegt eine Angststörung vor?", info)
        .attachAnswer(new MedicationAnswer("Nein"))
        .attachAnswer(
            new MedicationAnswer("Ja").followedBy(
                IS_PATIENT_YOUNGER_THAN(65, {
                    yes: new MedicationAnswer("Ja").followedBy(
                        ANTI_DEPRESSANT_SUBSCRIBED(
                            8,
                            false
                        )(info)
                            .attachAnswer(new MedicationAnswer("Nein"))
                            .attachAnswer(
                                new MedicationAnswer("Ja").followedBy(
                                    PatientSymptomsImproved(
                                        new MedicationAnswer("Nein").triggers([
                                            MedicationIndicators.POTENTIAL_OVER_MEDICATION_ANXIETY_1
                                        ]),
                                        {
                                            strategy:
                                                BoolQuestionAnswerStrategy.ON_NO
                                        }
                                    )(info)
                                )
                            )
                    ),
                    no: new MedicationAnswer("Nein").followedBy(
                        ANTI_DEPRESSANT_SUBSCRIBED(
                            12,
                            false
                        )(info)
                            .attachAnswer(new MedicationAnswer("Nein"))
                            .attachAnswer(
                                new MedicationAnswer("Ja").followedBy(
                                    PatientSymptomsImproved(
                                        new MedicationAnswer("Nein").triggers([
                                            MedicationIndicators.POTENTIAL_OVER_MEDICATION_ANXIETY_2
                                        ]),
                                        {
                                            strategy:
                                                BoolQuestionAnswerStrategy.ON_NO
                                        }
                                    )(info)
                                )
                            )
                    )
                })(info)
            )
        )

/*const PATIENT_HAS_INSOMNIA: QuestionCreator = (input) =>
    new MedicationQuestion(
        "Wird ein Antidepressivum nur wegen Schlaflosigkeit verordnet, ohne dass eine andere Indikation (Depression) vorliegt?",
        input
    )
        .attachAnswer(new MedicationAnswer("Nein"))
        .attachAnswer(
            new MedicationAnswer("Ja").followedBy(
                PatientTakesMedicine(
                    medSearch()
                        .and(SingleMedicationEntries.TCA, 50)
                        .and(SingleMedicationEntries.TRAZODON, 50)
                        .and(SingleMedicationEntries.MIRTAZAPIN, 30),
                    new MedicationAnswer("Ja").triggers([
                        MedicationIndicators.POTENTIAL_OVER_MEDICATION_INSOMNIA_1
                    ]),
                    {
                        strategy: BoolQuestionAnswerStrategy.ON_YES,
                        followedBy: new MedicationQuestion(
                            "Wird ein sedierendes Antidepressivum länger als 8 Wochen eingenommen?",
                            input
                        )
                            .attachAnswer(
                                new MedicationAnswer("Ja").triggers([
                                    MedicationIndicators.POTENTIAL_OVER_MEDICATION_INSOMNIA_2
                                ])
                            )
                            .attachAnswer(new MedicationAnswer("Nein"))
                    }
                )(input)
            )
        )*/

/*const PATIENT_HAS_PAIN: QuestionCreator = (input) =>
    new MedicationQuestion(
        "Wird ein Antidepressivum nur wegen Schmerzen verordnet, ohne dass eine andere Indikation (Depression) vorliegt?",
        input
    )
        .attachAnswer(new MedicationAnswer("Nein"))
        .attachAnswer(
            new MedicationAnswer("Ja").followedBy(
                PatientTakesMedicineForAReason(
                    medSearch()
                        .and(SingleMedicationEntries.TCA, 75)
                        .and(SingleMedicationEntries.VENLAFAXIN, 150)
                        .and(SingleMedicationEntries.MIRTAZAPIN)
                        .and(SingleMedicationEntries.SSRI),
                    "Wegen neuropathischer Schmerzen",
                    new MedicationAnswer("Ja").triggers([
                        MedicationIndicators.POTENTIAL_OVER_MEDICATION_PAIN_1
                    ]),
                    {
                        strategy: BoolQuestionAnswerStrategy.ON_YES,
                        followedBy: new MedicationQuestion(
                            "Wird ein Antidepressivum wegen unspezifischen Kreuzschmerzen eingenommen?",
                            input
                        )
                            .attachAnswer(
                                new MedicationAnswer("Ja").triggers([
                                    MedicationIndicators.POTENTIAL_OVER_MEDICATION_PAIN_2
                                ])
                            )
                            .attachAnswer(new MedicationAnswer("Nein"))
                            .followedBy(
                                PatientTakesMedicineForAReason(
                                    medSearch()
                                        .and(SingleMedicationEntries.TCA)
                                        .andGroup(MedicationGroups.SNRI),
                                    "Wegen Schmerzen, die nicht auf neuropathische Schmerzen, Spannungskopfschmerzen, Migräne oder das Fibromyalgie-Syndrom zurückzuführen sind?",
                                    new MedicationAnswer("Ja").triggers([
                                        MedicationIndicators.POTENTIAL_OVER_MEDICATION_PAIN_3
                                    ])
                                )(input)
                            )
                    }
                )(input)
            )
        )*/

const MedicationQuestionnaireBuilder: QuestionnaireCreator = (info) => [
    IS_AT_LEAST_ONE_ANTI_DEPRESSANT_TAKEN,
    IS_PHQ_SCORE_HIGHER_THAN(
        info.timestamp === QuestionnaireTimestamp.T0 ? 9 : 8,
        {
            yes: new MedicationAnswer("Ja").followedBy(
                PatientSymptomsImproved(
                    new MedicationAnswer("Ja").triggersIndicatorWithArgs(
                        MedicationIndicators.THERAPY_CONTROL,
                        [
                            String(info.phqScore),
                            getPhqScoreText(info.phqScore!),
                            "eine"
                        ]
                    )
                )(info).attachAnswer(
                    new MedicationAnswer("Nein").triggersIndicatorWithArgs(
                        MedicationIndicators.THERAPY_CONTROL,
                        [
                            String(info.phqScore),
                            getPhqScoreText(info.phqScore!),
                            "keine"
                        ]
                    )
                )
            )
        }
    ),
    PatientTakesMedicine(
        medSearch().and(SingleMedicationEntries.ANTI_DEPRESSANTS),
        new MedicationAnswer("Ja").followedBy(
            IS_PATIENT_YOUNGER_THAN(65, {
                yes: new MedicationAnswer("Ja").followedBy(
                    ANTI_DEPRESSANT_SUBSCRIBED(
                        4,
                        true
                    )(info)
                        .attachAnswer(
                            new MedicationAnswer("Ja").followedBy(
                                PatientSymptomsImproved(
                                    new MedicationAnswer("Nein").triggers([
                                        MedicationIndicators.POTENTIAL_OVER_MEDICATION_1
                                    ]),
                                    {
                                        strategy:
                                            BoolQuestionAnswerStrategy.ON_NO
                                    }
                                )(info)
                            )
                        )
                        .attachAnswer(new MedicationAnswer("Nein"))
                ),
                no: new MedicationAnswer("Nein").followedBy(
                    ANTI_DEPRESSANT_SUBSCRIBED(
                        6,
                        true
                    )(info)
                        .attachAnswer(
                            new MedicationAnswer("Ja").followedBy(
                                PatientSymptomsImproved(
                                    new MedicationAnswer("Nein").triggers([
                                        MedicationIndicators.POTENTIAL_OVER_MEDICATION_2
                                    ]),
                                    {
                                        strategy:
                                            BoolQuestionAnswerStrategy.ON_NO
                                    }
                                )(info)
                            )
                        )
                        .attachAnswer(new MedicationAnswer("Nein"))
                )
            })(info)
        ),
        { strategy: BoolQuestionAnswerStrategy.ON_YES },
        null,
        1
    ),
    FIRST_EPISODE_OF_LIGHT_DEPRESSION,
    FIRST_EPISODE_OF_SEVERE_DEPRESSION,
    TWO_OR_MORE_ANTI_DEPRESSANTS,
    TWO_OR_MORE_ANTI_DEPRESSANTS_2,
    PATIENT_HAS_ANXIETY,
    //TODO: Next should be integrated into anxiety?
    PatientTakesMedicine(
        medSearch().and(SingleMedicationEntries.BENZODIAZEPIN),
        new MedicationAnswer("Ja").followedBy(
            PatientTakesAtLeastNumberOfAntidepressants(
                1,
                new MedicationAnswer("Ja").followedBy(
                    PatientHasPreCondition(
                        PatientPreConditions.v1,
                        new MedicationAnswer("Ja").triggers([
                            MedicationIndicators.POTENTIAL_OVER_MEDICATION_ANXIETY_3
                        ])
                    )(info)
                )
            )(info)
        )
    ),

    //PATIENT_HAS_INSOMNIA,
    //PATIENT_HAS_PAIN,
    //9 Elements in total -> Index = 8
    ...RiskMedicationQuestionnaireBuilder
]
export default MedicationQuestionnaireBuilder
